import type { Category, Dish, DishFilters, Session, Stats } from "./types";

const SESSION_KEY = "family-menu-session";

export function loadSession(): Session | null {
  const raw = localStorage.getItem(SESSION_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as Session;
  } catch {
    localStorage.removeItem(SESSION_KEY);
    return null;
  }
}

export function saveSession(session: Session) {
  localStorage.setItem(SESSION_KEY, JSON.stringify(session));
}

export function clearSession() {
  localStorage.removeItem(SESSION_KEY);
}

type ApiOptions = RequestInit & {
  session?: Session | null;
};

async function api<T>(path: string, options: ApiOptions = {}): Promise<T> {
  const headers = new Headers(options.headers);
  if (!(options.body instanceof FormData)) {
    headers.set("Content-Type", "application/json");
  }
  if (options.session?.token) {
    headers.set("Authorization", `Bearer ${options.session.token}`);
  }

  const response = await fetch(path, {
    ...options,
    headers
  });

  if (!response.ok) {
    const message = await response.text();
    throw new Error(message || "请求失败，请稍后再试");
  }

  return response.json() as Promise<T>;
}

export function createHousehold(name: string, nickname: string) {
  return api<Session>("/api/households", {
    method: "POST",
    body: JSON.stringify({ name, nickname })
  });
}

export function joinHousehold(inviteCode: string, nickname: string) {
  return api<Session>("/api/join", {
    method: "POST",
    body: JSON.stringify({ inviteCode, nickname })
  });
}

export function getDishes(session: Session, filters: DishFilters) {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value) params.set(key, String(value));
  });
  return api<{ dishes: Dish[] }>(`/api/dishes?${params.toString()}`, { session });
}

export function getCategories(session: Session) {
  return api<{ categories: Category[] }>("/api/categories", { session });
}

export function addCategory(session: Session, name: string) {
  return api<{ category: Category }>("/api/categories", {
    method: "POST",
    body: JSON.stringify({ name }),
    session
  });
}

export function deleteCategory(session: Session, categoryId: string) {
  return api<{ ok: true }>(`/api/categories/${encodeURIComponent(categoryId)}`, {
    method: "DELETE",
    session
  });
}

export function createDish(session: Session, formData: FormData) {
  return api<{ dish: Dish }>("/api/dishes", {
    method: "POST",
    body: formData,
    session
  });
}

export function deleteDish(session: Session, dishId: string) {
  return api<{ ok: true }>(`/api/dishes/${encodeURIComponent(dishId)}`, {
    method: "DELETE",
    session
  });
}

export function orderMeal(session: Session, dishId: string, status: "ordered" | "cooked") {
  return api<{ mealId: string }>("/api/meals", {
    method: "POST",
    body: JSON.stringify({ dishId, status }),
    session
  });
}

export function addReview(
  session: Session,
  payload: { dishId: string; mealId?: string; rating: number; comment: string }
) {
  return api<{ reviewId: string }>("/api/reviews", {
    method: "POST",
    body: JSON.stringify(payload),
    session
  });
}

export function getStats(session: Session) {
  return api<Stats>("/api/stats", { session });
}
